import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { getAuth, onAuthStateChanged, User } from 'firebase/auth';
import humps from 'humps';
import store from '@/store';
import router from '@/router';

let isForceLoggingOut = false;

/**
 * Force logout: clear Vuex state, Firebase session, and redirect to /login.
 * Uses a flag to prevent multiple concurrent logouts.
 */
function forceLogout(): void {
  if (isForceLoggingOut) return;
  isForceLoggingOut = true;

  store.dispatch('signOut').finally(() => {
    router.replace('/login');
    isForceLoggingOut = false;
  });
}

/**
 * Dedicated service for backend-v2 API communication
 * Automatically handles Firebase ID token authentication with refresh logic
 * Automatically transforms snake_case responses to camelCase
 * Automatically transforms camelCase requests to snake_case
 */
class BackendV2Service {
  private axiosInstance: AxiosInstance;

  constructor() {
    const clientId = process.env.VUE_APP_CONTENT_MANAGER_CLIENT_ID;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (clientId) {
      headers['X-Client-ID'] = clientId;
    }

    this.axiosInstance = axios.create({
      baseURL: process.env.VUE_APP_BACKEND_V2_API_URL,
      headers,
      withCredentials: true,
      transformResponse: [
        ...axios.defaults.transformResponse as any[],
        (data) => humps.camelizeKeys(data),
      ],
      transformRequest: [
        (data) => humps.decamelizeKeys(data),
        ...axios.defaults.transformRequest as any[],
      ],
    });

    this.setupRequestInterceptor();
    this.setupResponseInterceptor();
  }

  /**
   * Wait for Firebase Auth to be ready and get the current user
   * Falls back to checking localStorage token if user is not immediately available
   */
  // eslint-disable-next-line class-methods-use-this
  private async getCurrentUser(): Promise<User | null> {
    const auth = getAuth();

    // If user is already available, return it
    if (auth.currentUser) {
      return auth.currentUser;
    }

    // Check if there's a token in localStorage (user might be logged in but Auth not initialized)
    const localStore = JSON.parse(localStorage.getItem('beereaders-enrollment-admin') || '{}');
    if (localStore.userToken && localStore.userLogged) {
      // Wait for auth state to be restored (max 2 seconds)
      return new Promise((resolve) => {
        const timeout = setTimeout(() => {
          resolve(auth.currentUser);
        }, 2000);

        const unsubscribe = onAuthStateChanged(auth, (user) => {
          clearTimeout(timeout);
          unsubscribe();
          resolve(user);
        });
      });
    }

    return null;
  }

  /**
   * Automatically adds Firebase ID token to all requests
   */
  private setupRequestInterceptor(): void {
    this.axiosInstance.interceptors.request.use(
      async (config: AxiosRequestConfig) => {
        const user = await this.getCurrentUser();

        if (user) {
          try {
            // getIdToken() uses cached token if not expired, otherwise refreshes
            const idToken = await user.getIdToken();
            // eslint-disable-next-line no-param-reassign
            config.headers = config.headers || {};
            // eslint-disable-next-line no-param-reassign
            config.headers.Authorization = `Bearer ${idToken}`;
          } catch (error) {
            console.error('Failed to get Firebase ID token:', error);
          }
        }

        return config;
      },
      (error) => Promise.reject(error),
    );
  }

  /**
   * Handles 401/403 errors by forcing token refresh and retrying.
   * If refresh fails or the retry also fails, forces a full logout.
   */
  private setupResponseInterceptor(): void {
    this.axiosInstance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        const status = error.response?.status;

        // If 401 or 403 and we haven't retried yet, force token refresh
        if ((status === 401 || status === 403) && !originalRequest?.retry) {
          originalRequest.retry = true;

          const user = await this.getCurrentUser();

          if (user) {
            try {
              // Force refresh the token
              const newToken = await user.getIdToken(true);
              // Update store so localStorage stays in sync
              store.commit('SET_USER_TOKEN', newToken);
              // Renew session cookie (non-blocking, best-effort)
              await this.createSession(newToken).catch((sessionError) => {
                console.error('Failed to refresh session cookie:', sessionError);
              });
              originalRequest.headers = originalRequest.headers || {};
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return this.axiosInstance.request(originalRequest);
            } catch (refreshError) {
              console.error('Failed to refresh Firebase token:', refreshError);
              forceLogout();
              return Promise.reject(refreshError);
            }
          }

          // No Firebase user available -> force logout
          forceLogout();
          return Promise.reject(error);
        }

        return Promise.reject(error);
      },
    );
  }

  /**
   * GET request
   */
  get<T = any>(url: string, config?: AxiosRequestConfig) {
    return this.axiosInstance.get<T>(url, config);
  }

  /**
   * POST request
   */
  post<T = any>(url: string, data?: any, config?: AxiosRequestConfig) {
    return this.axiosInstance.post<T>(url, data, config);
  }

  /**
   * PUT request
   */
  put<T = any>(url: string, data?: any, config?: AxiosRequestConfig) {
    return this.axiosInstance.put<T>(url, data, config);
  }

  /**
   * DELETE request
   */
  delete<T = any>(url: string, config?: AxiosRequestConfig) {
    return this.axiosInstance.delete<T>(url, config);
  }

  /**
   * PATCH request
   */
  patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig) {
    return this.axiosInstance.patch<T>(url, data, config);
  }

  /**
   * GET file as blob
   */
  getFile(url: string, config?: AxiosRequestConfig) {
    return this.axiosInstance.get(url, {
      ...config,
      responseType: 'blob',
    });
  }

  /**
   * Creates a backend session cookie by exchanging the Firebase ID token.
   * Extends session to 14 days instead of relying on the 1-hour Firebase token.
   */
  createSession(idToken: string): Promise<void> {
    return this.axiosInstance.post('/auth/session', JSON.stringify({ idToken }));
  }

  /**
   * Clears the backend session cookie.
   */
  clearSession(): Promise<void> {
    return this.axiosInstance.delete('/auth/session');
  }
}

export default new BackendV2Service();
